import React, { useEffect, useState } from 'react';
import { useGetIdentity, useNotify, usePermissions, useRecordContext, useRefresh, useTranslate } from 'react-admin';
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { SystemUpdate } from '@mui/icons-material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { hasPermission } from '../../utils/hasPermission';
import { isPlatformActor } from '../RequirePermission';
import type { KioskUpdateRecord } from './types';
import { errorLine } from '../kiosk-releases/labels';
import { RESTART_DEADLINE_MS, readerStateOf, updateStateLabel } from './updateLabels';
import { fingerprintOf } from '../kiosk-release-keys/releaseKeyCrypto';
import type { ReleaseKey } from '../kiosk-release-keys/types';

const RELEASE_KEY_ID = /^rk-[0-9a-f]{24}$/;

type RestartTarget = 'app' | 'pc';

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" component="div">{value}</Typography>
  </Box>
);

export const KioskUpdatePanel = () => {
  const record = useRecordContext<KioskUpdateRecord>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const { identity, isLoading: identityLoading } = useGetIdentity();
  const [confirm, setConfirm] = useState<RestartTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeKeyId, setActiveKeyId] = useState<string | null | undefined>(undefined);
  const granted = permissions as string[] | null | undefined;
  const vendorIds = ((identity as any)?.vendorIds as string[] | undefined) || [];
  const platformReader = !identityLoading && identity !== undefined && isPlatformActor(granted, vendorIds) && hasPermission(granted, 'kiosk-releases:read:all');
  useEffect(() => {
    if (!platformReader) return;
    let live = true;
    apiClient.get<{ data: ReleaseKey[] }>('/api/backend/kiosk-release-keys').then(
      (answer) => {
        if (!live) return;
        const active = answer.data.find((key) => key.status === 'active');
        setActiveKeyId(active === undefined ? null : active.keyId);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [platformReader]);
  if (!record || !('lastRestart' in record)) return null;
  const u = (key: string, options?: any) => translate(`resources.kiosk-devices.update.${key}`, options);
  const r = (key: string, options?: any) => translate(`resources.kiosk-releases.${key}`, options);
  const platformWriter = isPlatformActor(granted, vendorIds) && hasPermission(granted, 'kiosk-releases:write:all');
  const retryable = record.currentInstall !== null && (record.currentInstall.state === 'failed' || record.currentInstall.state === 'rolled_back') ? record.currentInstall : null;
  const canRestart = hasPermission(granted, 'kiosk-devices:write:all');
  const base = `${API_CONFIG.endpoints.backend.kioskDevices}/${record.id}`;
  const stateLine = updateStateLabel(record.updateState, translate);
  const reader = readerStateOf(record.health);
  const readerReconnecting = reader === 'connecting';
  const open = record.openRestart;
  const pendingWaitsForReader = open !== null && open.status === 'pending' && !open.skipReaderCheck && readerReconnecting;

  const restartLine = (row: NonNullable<KioskUpdateRecord['openRestart']>) => {
    if (row.status === 'in_progress') return u('restartInProgress');
    if (row.deadlineAt === null) return u('restartPendingNoTime');
    return u('restartPending', { time: new Date(new Date(row.deadlineAt).getTime() - RESTART_DEADLINE_MS).toLocaleString() });
  };

  const retry = async (releaseId: string) => {
    setBusy(true);
    try {
      await apiClient.post(`${base}/update-retry`, { releaseId });
      notify(u('retried'), { type: 'success' });
    } catch (error) {
      const apiError = error as ApiError;
      const reason = apiError.details?.reason;
      notify(typeof reason === 'string' ? u(`retryRefused.${reason}`, { _: r('errors.KIOSK_UPDATE_RETRY_REFUSED') }) : r('errors.generic'), { type: 'error' });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const restart = async (target: RestartTarget, skipReaderCheck: boolean) => {
    setBusy(true);
    try {
      await apiClient.post(`${base}/restart`, { target, skipReaderCheck });
      notify(u('restartRequested'), { type: 'success' });
      setConfirm(null);
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SystemUpdate sx={{ mr: 2, color: '#00C853' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{u('title')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
          <Field label={translate('resources.kiosk-devices.fields.appVersion')} value={record.appVersion || '-'} />
          <Field label={translate('resources.kiosk-devices.fields.launcherVersion')} value={record.launcherVersion || '-'} />
          <Field label={u('ocrlReleaseVersion')} value={record.ocrlReleaseVersion || '-'} />
          <Field label={u('targetVersion')} value={record.targetRelease === null ? '-' : `${r(`kinds.${record.targetRelease.kind}`)} ${record.targetRelease.version}`} />
          <Field label={translate('resources.kiosk-devices.fields.updateState')} value={stateLine} />
          <Field
            label={u('reader')}
            value={
              <>
                {u(`readerStates.${reader === null ? 'none' : reader}`)}
                {record.lastHeartbeatAt !== null && (
                  <Typography variant="caption" color="text.secondary" component="div">{u('readerReported', { time: new Date(record.lastHeartbeatAt).toLocaleString() })}</Typography>
                )}
              </>
            }
          />
          <Field
            label={u('releaseKey')}
            value={
              record.releaseKeyId === null ? u('releaseKeyNone') : (
                <>
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>{RELEASE_KEY_ID.test(record.releaseKeyId) ? fingerprintOf(record.releaseKeyId) : record.releaseKeyId}</Box>
                  {activeKeyId !== undefined && (
                    <Typography variant="caption" color={activeKeyId === record.releaseKeyId ? 'text.secondary' : 'error'} component="div">
                      {activeKeyId === record.releaseKeyId ? u('releaseKeyCurrent') : u('releaseKeyOther')}
                    </Typography>
                  )}
                </>
              )
            }
          />
          {record.updateWaitingReason !== null && (
            <Field label={u('waitingBecause')} value={record.updateWaitingReason === 'not_before' ? u('waitingNotBefore') : r(`waitingReasons.${record.updateWaitingReason}`)} />
          )}
        </Box>
        {record.inTestGroup && <Box sx={{ mb: 2 }}><Chip size="small" color="warning" label={u('inTestGroup')} /></Box>}
        {record.updateError !== null && <Typography variant="body2" color="error" sx={{ mb: 2 }}>{record.updateError}</Typography>}
        {open !== null && (
          <Box sx={{ mb: 2 }} data-testid="restart-pending">
            <Typography variant="body2">{restartLine(open)}</Typography>
            {canRestart && pendingWaitsForReader && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">{u('pendingWaitsForReader')}</Typography>
                <Button size="small" variant="outlined" disabled={busy} onClick={() => restart(open.type === 'restart' ? 'app' : 'pc', true)} sx={{ textTransform: 'none' }}>{u('restartNow')}</Button>
              </Box>
            )}
          </Box>
        )}
        {record.lastRestart !== null && record.openRestart === null && (
          <Box sx={{ mb: 2 }} data-testid="last-restart">
            <Typography variant="caption" color="text.secondary">{u('lastRestart.title')}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2">{u(`lastRestart.types.${record.lastRestart.type}`)}</Typography>
              <Typography variant="body2" color={record.lastRestart.status === 'failed' ? 'error' : 'text.primary'}>{u(`lastRestart.statuses.${record.lastRestart.status}`)}</Typography>
              <Typography variant="body2" color="text.secondary">{u('lastRestart.requested', { time: new Date(record.lastRestart.requestedAt).toLocaleString() })}</Typography>
              {record.lastRestart.closedAt !== null && <Typography variant="body2" color="text.secondary">{u('lastRestart.closed', { time: new Date(record.lastRestart.closedAt).toLocaleString() })}</Typography>}
            </Box>
            {record.lastRestart.status === 'expired' && <Typography variant="body2">{u('lastRestart.expired')}</Typography>}
            {record.lastRestart.status === 'failed' && record.lastRestart.reason !== null && <Typography variant="body2" color="error">{u(`lastRestart.reasons.${record.lastRestart.reason}`)}</Typography>}
            {record.lastRestart.status === 'failed' && record.lastRestart.detail !== null && <Typography variant="body2" color="text.secondary">{record.lastRestart.detail}</Typography>}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {platformWriter && retryable !== null && (
            <>
              <Button variant="outlined" disabled={busy} onClick={() => retry(retryable.releaseId)} sx={{ textTransform: 'none' }}>{u('retry')}</Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>{u('retriesVersion', { version: retryable.version })}</Typography>
            </>
          )}
          {canRestart && record.openRestart === null && (
            <>
              <Button variant="outlined" disabled={busy} onClick={() => setConfirm('app')} sx={{ textTransform: 'none' }}>{u('restartApp')}</Button>
              <Button variant="outlined" color="warning" disabled={busy} onClick={() => setConfirm('pc')} sx={{ textTransform: 'none' }}>{u('restartPc')}</Button>
            </>
          )}
        </Box>
        <Dialog open={confirm !== null} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{confirm === 'pc' ? u('restartPc') : u('restartApp')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{confirm === 'pc' ? u('restartPcConfirm') : u('restartAppConfirm')}</Typography>
            {readerReconnecting && <Typography variant="body2" sx={{ mt: 2 }}>{u('readerNotReady')}</Typography>}
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
            {!readerReconnecting ? (
              <Button variant="contained" disabled={busy} onClick={() => confirm !== null && restart(confirm, false)} sx={{ textTransform: 'none' }}>
                {confirm === 'pc' ? u('restartPc') : u('restartApp')}
              </Button>
            ) : (
              <>
                <Button variant="outlined" disabled={busy} onClick={() => confirm !== null && restart(confirm, false)} sx={{ textTransform: 'none' }}>{u('waitForReader')}</Button>
                <Button variant="contained" disabled={busy} onClick={() => confirm !== null && restart(confirm, true)} sx={{ textTransform: 'none' }}>{u('restartNow')}</Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
